import { useState, useEffect, useCallback, useMemo, useRef } from "react";

const MIN_WIDTH = 30;
const MAX_WIDTH = 2000;

interface UseColumnResizeOptions {
  storageKey: string;
  defaultWidths: Record<string, number>;
  enabled?: boolean;
  /**
   * The width a column ACTUALLY renders at, measured from the DOM. Optional,
   * and only needed by tables that let one column float (no width on its
   * `<col>`, so it soaks up the remainder): for that column the stored number
   * is fiction, and seeding a drag from it clamps the handle against a width
   * the user never saw — the divider stops dead long before the column does.
   * Return `undefined` to fall back to the stored width.
   */
  measureWidth?: (colId: string) => number | undefined;
}

function loadWidths(
  storageKey: string,
  defaultWidths: Record<string, number>,
): Record<string, number> {
  if (typeof window === "undefined") return { ...defaultWidths };
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (
        typeof parsed !== "object" ||
        parsed === null ||
        Array.isArray(parsed)
      )
        return { ...defaultWidths };
      const validated: Record<string, number> = {};
      for (const [key, val] of Object.entries(
        parsed as Record<string, unknown>,
      )) {
        if (
          typeof val === "number" &&
          Number.isFinite(val) &&
          val >= MIN_WIDTH &&
          val <= MAX_WIDTH
        ) {
          validated[key] = val;
        }
      }
      return { ...defaultWidths, ...validated };
    }
  } catch {
    // ignore corrupted localStorage
  }
  return { ...defaultWidths };
}

interface ResizingState {
  leftCol: string;
  rightCol: string;
  leftStart: number;
  rightStart: number;
}

export function useColumnResize({
  storageKey,
  defaultWidths,
  enabled = true,
  measureWidth,
}: UseColumnResizeOptions) {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    loadWidths(storageKey, defaultWidths),
  );
  const [resizing, setResizing] = useState<ResizingState | null>(null);
  const startXRef = useRef(0);

  // Reload widths when storageKey or defaultWidths change
  const defaultWidthsJson = JSON.stringify(defaultWidths);
  useEffect(() => {
    setColumnWidths(loadWidths(storageKey, JSON.parse(defaultWidthsJson)));
  }, [storageKey, defaultWidthsJson]);

  // Keep a ref in sync for stable callbacks and flush-on-unmount
  const columnWidthsRef = useRef(columnWidths);
  columnWidthsRef.current = columnWidths;

  // Persist to localStorage (debounced)
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(columnWidths));
      } catch {
        // Storage unavailable (quota exceeded, private browsing, etc.)
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [columnWidths, enabled, storageKey]);

  // Flush on unmount
  useEffect(() => {
    if (!enabled) return;
    return () => {
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem(
            storageKey,
            JSON.stringify(columnWidthsRef.current),
          );
        } catch {
          // Storage unavailable
        }
      }
    };
  }, [enabled, storageKey]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, leftCol: string, rightCol: string) => {
      e.preventDefault();
      e.stopPropagation();
      startXRef.current = e.clientX;
      const widths = columnWidthsRef.current;
      // The width the divider starts from must be the width the user SEES —
      // the drag maps mouse travel onto it 1:1 and clamps at MIN_WIDTH against
      // it. `measureWidth` wins when the table can supply it.
      const startWidth = (col: string) =>
        measureWidth?.(col) ?? widths[col] ?? defaultWidths[col] ?? 100;
      setResizing({
        leftCol,
        rightCol,
        leftStart: startWidth(leftCol),
        rightStart: startWidth(rightCol),
      });
    },
    [defaultWidths, measureWidth],
  );

  useEffect(() => {
    if (!enabled || !resizing) return;

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      const diff = e.clientX - startXRef.current;
      const combined = resizing.leftStart + resizing.rightStart;

      let newLeft = resizing.leftStart + diff;
      let newRight = combined - newLeft;

      if (newLeft < MIN_WIDTH) {
        newLeft = MIN_WIDTH;
        newRight = combined - MIN_WIDTH;
      }
      if (newRight < MIN_WIDTH) {
        newRight = MIN_WIDTH;
        newLeft = combined - MIN_WIDTH;
      }

      setColumnWidths((prev) => ({
        ...prev,
        [resizing.leftCol]: newLeft,
        [resizing.rightCol]: newRight,
      }));
    };

    const handleMouseUp = () => {
      setResizing(null);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [enabled, resizing]);

  const getWidth = useCallback(
    (colId: string): number => {
      return columnWidths[colId] ?? defaultWidths[colId] ?? 100;
    },
    [columnWidths, defaultWidths],
  );

  const resizingCols = useMemo(
    () =>
      resizing
        ? { leftCol: resizing.leftCol, rightCol: resizing.rightCol }
        : null,
    [resizing],
  );

  return useMemo(
    () => ({
      getWidth,
      resizingCols,
      handleResizeStart,
      setColumnWidths,
    }),
    [getWidth, resizingCols, handleResizeStart],
  );
}
